import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    if (this.classList.contains('dripstock-low-stock')) {
      closestSection?.addEventListener('click', this.#handleColorPress);
      closestSection?.addEventListener('change', this.#handleColorChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    if (this.classList.contains('dripstock-low-stock')) {
      closestSection?.removeEventListener('click', this.#handleColorPress);
      closestSection?.removeEventListener('change', this.#handleColorChange);
    }
  }

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  // Show one clear, immediately visible callout as soon as an eligible color is tapped.
  // This does not manufacture a stock count; a later Shopify section response
  // may replace it with the real low-stock quantity where inventory is tracked.
  #handleColorPress = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const selector = 'input[data-option-name="Color"], input[data-option-name="Colour"]';
    const input = target.matches(selector) ? target : target.closest('label')?.querySelector(selector);
    if (!(input instanceof HTMLInputElement)) return;
    if (!this.closest('.product-details')?.contains(input)) return;
    this.#showColorCallout(input.value, !input.disabled && input.dataset.optionAvailable !== 'false');
  };

  #handleColorChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!this.closest('.product-details')?.contains(target)) return;

    if (target instanceof HTMLInputElement && /^(color|colour)$/i.test(target.dataset.optionName ?? '')) {
      this.#showColorCallout(target.value, !target.disabled && target.dataset.optionAvailable !== 'false');
    } else if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex];
      if (option && /^(color|colour)$/i.test(option.dataset.optionName ?? '')) {
        this.#showColorCallout(option.value, !option.disabled);
      }
    }
  };

  #showColorCallout = (value, available) => {
    const color = String(value ?? '').trim();
    const mainColor = color.split('/')[0].trim().toLowerCase();
    const eligible = ['black', 'gray', 'grey', 'navy', 'charcoal'].some((name) => mainColor.includes(name));

    if (!eligible || !available) {
      this.removeAttribute('data-color-urgency-active');
      this.querySelector('.dripstock-low-stock__badge')?.remove();
      return;
    }

    this.setAttribute('data-color-urgency-active', 'true');
    let badge = this.querySelector('.dripstock-low-stock__badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'dripstock-low-stock__badge';
      badge.setAttribute('role', 'status');
      badge.setAttribute('aria-live', 'polite');
      badge.setAttribute('aria-atomic', 'true');
      this.appendChild(badge);
    }
    // Replace any prior color's message while the selected variant is loading.
    // No implied scarcity or fictional inventory is introduced here.
    badge.textContent = '⚡ ORDER ' + color.toUpperCase() + ' NOW';
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
